import { useState } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { BoxHeader } from "@/components/design/BoxHeader";
import { QuickActionLink } from "@/components/dashboard/StatTile";
import { NoBackend } from "@/components/common/NoBackend";
import { userErrorMessage } from "@/lib/user-error";
import {
  PublishedQuestionnaireGroupPartsDocument,
  QuestionnaireGroupEvaluationDocument,
} from "@/graphql/generated/operations";
import {
  ELEMENT_CODES,
  ELEMENT_GROUPS,
  GROUP_ORDER,
  elementDisplayName,
  elementImageSrc,
  elementNameId,
} from "@/lib/elements";
import { elementEvaluation } from "@/lib/element-evaluations";

// Result screen for one questionnaire group (genesis#183, spark#196). Reachable
// once every part of that group is answered; the gate reuses the per-part
// `answered` flags rather than a second completion query. Membership is the
// group's, so there is no fixed part count to compare against any more - and the
// evaluation is scoped to the group instead of to every element-free
// questionnaire, which is what used to adopt unrelated questionnaires.
//
// The only screen that splits the merged self-report score into its instruments.
// `selfAssessmentResults.result` is merged as well, so the Power Map and the
// collage have already moved - what this screen adds is which instrument produced
// the value. Only the medal stays behind, it gates on the self-assessment
// counters. `selfScoreBefore` is null for elements without a self-assessment; the
// quiz result is then the first self-report value that power ever had, which the
// row states instead of drawing a delta from nothing.

type Row = {
  element: string;
  quizResult: number | null;
  selfScoreBefore: number | null;
  selfScoreWithQuiz: number | null;
  quizResultText: string | null;
  answeredItemCount: number;
  itemCount: number;
};

function Delta({ from, to }: { from: number; to: number }) {
  const diff = to - from;
  if (diff === 0) {
    return (
      <span className="text-[11px] tabular-nums text-ink/55">
        <FormattedMessage id="grow.play.quiz.result.unchanged" defaultMessage="unchanged" />
      </span>
    );
  }
  // Direction is monochrome on purpose: colour is reserved for destructive
  // signals, and a lower self-report value is not an error.
  return (
    <span className="text-[11px] tabular-nums text-ink/55">
      {diff > 0 ? "↑" : "↓"} {Math.abs(diff)}
    </span>
  );
}

// Page-colored halo instead of a chip, so the numbers stay readable on the
// artwork without covering it (same device as PowerTile's level word).
const NUMBER_HALO = {
  textShadow: "0 0 4px var(--color-page), 0 0 10px var(--color-page)",
} as const;

// The right-hand side of a row: what the quiz did to this power.
function Scores({ row }: { row: Row }) {
  const before = row.selfScoreBefore;
  const after = row.selfScoreWithQuiz;

  // Whether the quiz said anything about this power is `quizResult`, not the item
  // counter: the counter is scoped to answer choices you can still answer, so an
  // author replacing one drops it to 0 while the score keeps the rating.
  if (row.quizResult == null) {
    return (
      <span className="text-[11px] text-ink/55">
        <FormattedMessage
          id="grow.play.quiz.result.noQuizItems"
          defaultMessage="not covered by the quiz"
        />
      </span>
    );
  }
  if (before == null) {
    return (
      <span className="flex items-baseline gap-2" style={NUMBER_HALO}>
        <span className="text-[13px] tabular-nums text-ink/80">{after}</span>
        <span className="text-[10px] text-ink/55">
          <FormattedMessage
            id="grow.play.quiz.result.firstValue"
            defaultMessage="first self-report value"
          />
        </span>
      </span>
    );
  }
  return (
    <span className="flex items-baseline gap-2" style={NUMBER_HALO}>
      <span className="text-[13px] tabular-nums text-ink/55">{before}</span>
      <span className="text-[11px] text-ink/45" aria-hidden>
        →
      </span>
      <span className="text-[13px] tabular-nums text-ink/80">{after}</span>
      {after != null ? <Delta from={before} to={after} /> : null}
    </span>
  );
}

// Horizontal counterpart to GrowElementCard's vertical scrim: dense on the left
// where the name sits, thinning towards the numbers so the
// artwork carries the right half without swallowing the digits.
const ROW_SCRIM =
  "linear-gradient(to right," +
  " color-mix(in srgb, var(--color-page) 95%, transparent) 0%," +
  " color-mix(in srgb, var(--color-page) 88%, transparent) 40%," +
  " color-mix(in srgb, var(--color-page) 62%, transparent) 100%)";

// The artwork encodes what the quiz did, it is not decoration: powers the quiz
// moved show their motif, powers it confirmed fade back, powers it never asked
// about show none. Blurred layers scale up so the blur edge cannot bleed a seam
// into the row (same trick as PowerTile). `active` (pinned) lifts a row to full
// artwork; hover does the same through group-hover variants, which Tailwind
// emits after the base utilities and therefore win without `!`.
function artworkClasses(row: Row, active: boolean): string | null {
  if (row.quizResult == null) return null;
  if (active) return "opacity-100 blur-none scale-100";
  const before = row.selfScoreBefore;
  const after = row.selfScoreWithQuiz;
  if (before == null) return "opacity-85 blur-none scale-100";
  if (after != null && after !== before) return "opacity-100 blur-none scale-100";
  return "opacity-20 blur-[3px] scale-110";
}

function ElementRow({
  row,
  pinned,
  onPinToggle,
}: {
  row: Row;
  pinned: boolean;
  onPinToggle: () => void;
}) {
  const intl = useIntl();
  const name = intl.formatMessage({
    id: elementNameId(row.element),
    defaultMessage: elementDisplayName(row.element),
  });
  // The long text explains what the number means. Banded on the merged score, so
  // it describes the value shown. genesis's short band label (resultText) is not
  // drawn next to the name: it is a German-only server string that doubled the
  // number in words and broke the English page.
  const evaluation = elementEvaluation(row.element, row.selfScoreWithQuiz, intl.locale);

  const src = elementImageSrc(row.element);
  const art = artworkClasses(row, pinned);
  const powerHref = `/grow/power/${ELEMENT_CODES[row.element] ?? ""}`;
  const header = (
    <>
      <span className="min-w-0 text-sm font-medium">{name}</span>
      <span className="shrink-0">
        <Scores row={row} />
      </span>
    </>
  );

  return (
    <div className="group bg-page box-flat relative overflow-hidden transition hover:ring-ink/15">
      {src && art ? (
        <>
          <img
            src={src}
            alt=""
            aria-hidden
            loading="lazy"
            className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-all duration-500 ease-out group-hover:scale-100 group-hover:opacity-100 group-hover:blur-none ${art}`}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{ backgroundImage: ROW_SCRIM }}
          />
        </>
      ) : null}

      <div className="relative">
        {evaluation ? (
          // Hover opens the body, a click pins it open, like the Save dashboard's
          // StatTile - except the pin survives moving the mouse to another row.
          // aria-expanded tracks the pin only: hover is not a semantic state.
          <button
            type="button"
            onClick={onPinToggle}
            aria-expanded={pinned}
            className="flex w-full items-baseline justify-between gap-3 px-5 py-3 text-left"
          >
            {header}
          </button>
        ) : (
          // No long text to reveal, so the whole row is the jump to the power.
          <Link
            to={powerHref}
            className="flex items-baseline justify-between gap-3 px-5 py-3 hover:text-ink"
          >
            {header}
          </Link>
        )}

        {evaluation ? (
          <div
            className={`grid transition-all duration-500 ease-out ${
              pinned
                ? "grid-rows-[1fr] opacity-100"
                : "grid-rows-[0fr] opacity-0 group-hover:grid-rows-[1fr] group-hover:opacity-100 group-focus-within:grid-rows-[1fr] group-focus-within:opacity-100"
            }`}
          >
            <div className="min-h-0 overflow-hidden px-5 pb-4">
              <p className="text-sm leading-relaxed text-ink/80">{evaluation}</p>
              {row.quizResult != null ? (
                <p className="mt-3 text-[11px] text-ink/55">
                  {row.selfScoreBefore != null ? (
                    <FormattedMessage
                      id="grow.play.quiz.result.quizAlone"
                      defaultMessage="Quiz on its own: {score}{band, select, none {} other { · {band}}}{items, select, none {} other {, from {answered} of {total} items}}."
                      values={{
                        score: row.quizResult,
                        band: row.quizResultText ?? "none",
                        items: row.answeredItemCount > 0 ? "some" : "none",
                        answered: row.answeredItemCount,
                        total: row.itemCount,
                      }}
                    />
                  ) : (
                    // Without a self-assessment the merged score IS the quiz score, so
                    // repeating it here would print the same number and band twice - the
                    // row header already carries both. Only the item origin is new.
                    <FormattedMessage
                      id="grow.play.quiz.result.quizOnlySource"
                      defaultMessage="From the quiz alone{items, select, none {} other {, {answered} of {total} items}}."
                      values={{
                        items: row.answeredItemCount > 0 ? "some" : "none",
                        answered: row.answeredItemCount,
                        total: row.itemCount,
                      }}
                    />
                  )}
                </p>
              ) : null}
              <div className="mt-3">
                <QuickActionLink to={powerHref} icon="→">
                  <FormattedMessage
                    id="grow.play.quiz.result.openPower"
                    defaultMessage="Open this power"
                  />
                </QuickActionLink>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function QuestionnaireGroupResultPage() {
  const intl = useIntl();
  const { questionnaireGroupId = "" } = useParams();
  // Hover previews a row, a click pins it. The pin survives hovering elsewhere
  // and only moves when another row is clicked, so a chosen power stays readable.
  const [pinned, setPinned] = useState<string | null>(null);

  const { data: partsData, loading: partsLoading } = useQuery(
    PublishedQuestionnaireGroupPartsDocument,
    { fetchPolicy: "cache-and-network" },
  );
  // The scores are recomputed on every answer, so never serve them from cache.
  const { data, loading, error } = useQuery(QuestionnaireGroupEvaluationDocument, {
    variables: { questionnaireGroupId },
    fetchPolicy: "cache-and-network",
  });

  const group = (partsData?.publishedQuestionnaireGroups ?? []).find(
    (g) => g.questionnaireGroupId === questionnaireGroupId,
  );
  const parts = group?.parts ?? [];
  const complete = parts.length > 0 && parts.every((p) => p.answered);
  const rows = (data?.questionnaireGroupEvaluation ?? []) as Row[];
  const byElement = new Map(rows.map((r) => [r.element, r]));

  return (
    <section
      className="mx-auto max-w-screen-md px-4 py-12"
      data-graphql="PublishedQuestionnaireGroupParts, QuestionnaireGroupEvaluation"
    >
      <div className="mb-5">
        <BackButton />
      </div>

      <header className="mb-8">
        <p className="text-xs tracking-wide text-ink/55">
          <FormattedMessage id="grow.play.quiz.eyebrow" defaultMessage="Grow · Play" />
        </p>
        <h1 className="mt-1 text-3xl font-semibold">
          <FormattedMessage id="grow.play.quiz.result.title" defaultMessage="Your result" />
        </h1>
        <p className="mt-2 text-sm text-ink/55">
          <FormattedMessage
            id="grow.play.quiz.result.subtitle"
            defaultMessage="How the parts you answered moved your powers. They count towards your self-report score, weighted by how many items they contribute."
          />
        </p>
      </header>

      {partsLoading && parts.length === 0 ? (
        <p className="py-8 text-sm text-ink/55">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      ) : parts.length === 0 ? (
        <NoBackend
          label={intl.formatMessage({
            id: "grow.play.group.missing",
            defaultMessage: "This questionnaire is not available on this environment.",
          })}
        />
      ) : !complete ? (
        <div className="bg-surface box p-5">
          <p className="text-sm text-ink/70">
            <FormattedMessage
              id="grow.play.quiz.result.locked"
              defaultMessage="The result opens once all parts are answered."
            />
          </p>
          <Link
            to={`/grow/play/group/${questionnaireGroupId}`}
            className="mt-4 inline-block text-[11px] text-ink/55 underline underline-offset-4 hover:text-ink"
          >
            <FormattedMessage
              id="grow.play.quiz.result.backToParts"
              defaultMessage="Back to the parts"
            />
          </Link>
        </div>
      ) : error ? (
        <div className="bg-surface box p-5">
          <p className="text-sm text-ink/70">
            <FormattedMessage
              id="grow.play.quiz.result.error"
              defaultMessage="The result could not be loaded: {message}"
              values={{ message: userErrorMessage(intl, error) }}
            />
          </p>
        </div>
      ) : loading && rows.length === 0 ? (
        <p className="py-8 text-sm text-ink/55">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      ) : rows.length === 0 ? (
        <div className="bg-surface box p-5">
          <p className="text-sm text-ink/70">
            <FormattedMessage
              id="grow.play.quiz.result.empty"
              defaultMessage="No powers to show yet."
            />
          </p>
        </div>
      ) : (
        <div className="space-y-4 pb-24">
          {GROUP_ORDER.map((group) => {
            const groupRows = ELEMENT_GROUPS[group].elements
              .map((e) => byElement.get(e))
              .filter((r): r is Row => r != null);
            if (groupRows.length === 0) return null;
            return (
              <div key={group} className="bg-surface box p-5">
                <BoxHeader
                  title={intl.formatMessage({ id: ELEMENT_GROUPS[group].labelId })}
                />
                <div className="space-y-2">
                  {groupRows.map((r) => (
                    <ElementRow
                      key={r.element}
                      row={r}
                      pinned={pinned === r.element}
                      onPinToggle={() => setPinned((p) => (p === r.element ? null : r.element))}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
